// Summary before detail: per phase, how much is open, how much is overdue,
// and when the phase's own last item is planned to finish. Scanned before the
// chart or the table are read, same reasoning as every other summary-first
// screen in this cockpit.
import { CalendarClock, TriangleAlert } from 'lucide-react'
import { Badge } from '../../lib/shadcn/badge'
import { cn } from '../../lib/shadcn/utils'
import type { PhaseSummary } from '../../utils/devPlan'

export function PhaseSummaryStrip({ phases }: { phases: PhaseSummary[] }) {
  if (phases.length === 0) {
    return <p className="text-sm text-muted-foreground">No phases on record.</p>
  }
  const sorted = [...phases].sort((a, b) => a.sort_order - b.sort_order)
  return (
    <ul className="flex flex-wrap gap-2">
      {sorted.map((p) => (
        <li
          key={p.phase_key}
          className={cn(
            'min-w-[11rem] flex-1 rounded-md border bg-background px-3 py-2',
            p.overdue_count > 0 ? 'border-destructive/50' : 'border-border',
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{p.label}</span>
            <span className="text-xs tabular-nums text-muted-foreground">{p.item_count} items</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="tabular-nums">{p.not_started_count} not started</span>
            <span className="tabular-nums">{p.in_progress_count} in progress</span>
            <span className="tabular-nums">{p.done_count} done</span>
            {p.blocked_count > 0 && (
              <Badge variant="destructive">{p.blocked_count} blocked</Badge>
            )}
            {p.awaiting_decision_count > 0 && (
              <Badge variant="warning">{p.awaiting_decision_count} awaiting decision</Badge>
            )}
            {p.on_hold_count > 0 && <Badge variant="secondary">{p.on_hold_count} on hold</Badge>}
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
            <span className="flex items-center gap-1 text-muted-foreground">
              <CalendarClock className="h-3 w-3 shrink-0" />
              {/* The old task board's own wording for a phase with no target date on
                  its last open item. Kept deliberately: see the build spec's rule that
                  a missing figure renders as missing, never as "On time". */}
              {p.last_planned_end ? (
                <span className="tabular-nums">last planned {p.last_planned_end}</span>
              ) : (
                'No target'
              )}
            </span>
            <span className="tabular-nums text-muted-foreground">
              {p.effort_days_remaining} days left
            </span>
          </div>
          {p.overdue_count > 0 && (
            <div className="mt-1.5">
              <Badge variant="destructive" className="gap-1">
                <TriangleAlert className="h-3 w-3" /> {p.overdue_count} past their date
              </Badge>
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}
